import React from 'react'
import { serverUrl } from '@utils/MkEnv'
import { ELocationType, ISearchLocation, MkSavedSearch, SaveSearchPayload } from '@typescript'
import { API } from '@constants'
import { compareDates } from '@utils/compareDates'
import { formatDateRange } from '@utils/formatDateRange'
import { fetchFilters as apiFetchFilters } from '@api/MkApi'
import { logger } from '@utils/Logger'
import { countResortsWithAvailabilityFromResortList, mergeShowAvailableOnlyCount } from '@utils/countResortsWithAvailableSuites'
import { AvailabilityAction, AvailabilityActionType, AvailabilityState, IAvailabilityService, TMkAvailableSuite } from './types'
import { AvailabilityContextProps } from './context'
import { MkFilterCategory, MkResortAvailRequest, MkResortAvailResponse, MkSearchRequest, MkSearchResponse } from './services/searchTypes'
import { searchResortAvail } from './services/MkApiSearch'
import { ACCESSIBILITY_FILTER_NAME, API_SEARCH_RESORT_LIST, FILTER_SECTION_SPECIAL, SHOW_AVAILABLE_RESORTS_ONLY } from './services/MkSearchConstants'
import { initializeAvailabilityState } from './state'
import { buildFlexiDatesString } from './utils/formatFlexibleDates'
import { buildFiltersFromCategories } from './utils/filtersShape'
import type { Filters } from './components/FilterPanel/FilterPanel'

export default class AvailabilityService implements IAvailabilityService {
  private state: AvailabilityState

  private dispatch: React.Dispatch<AvailabilityAction>

  private getAuthHeaders: () => Record<string, string>

  private isAuthorized: () => boolean

  constructor(state: AvailabilityState, dispatch: React.Dispatch<AvailabilityAction>, getAuthHeaders: () => Record<string, string>, isAuthorized: () => boolean) {
    this.state = state
    this.dispatch = dispatch
    this.getAuthHeaders = getAuthHeaders
    this.isAuthorized = isAuthorized
  }

  public static initialState(): AvailabilityState {
    return initializeAvailabilityState()
  }

  // Batch update for location, dates, and filters
  public batchUpdateSearchCriteria(
    location?: ISearchLocation | null,
    dates?: { startDate: Date | null; endDate: Date | null } | null,
    isFlexibleDates?: boolean,
    isBonusTimeDates?: boolean,
    flexiDatesOptions?: {
      flexiDatesString?: string
      selectedMonths?: Date[]
      numberOfNights?: number
    } | null,
    bonusTimeOptions?: {
      bonusTimeDatesString?: string
      numberOfNights?: number
    } | null,
    filterList?: MkFilterCategory[] | null,
    isDatesBufferEnabled?: boolean | null,
  ): void {
    const updates: AvailabilityAction[] = []

    updates.push({ type: AvailabilityActionType.SET_IS_SAVED_SEARCH_EXECUTION, payload: true })

    // Update location if provided
    if (location) {
      updates.push({ type: AvailabilityActionType.SET_LOCATION, payload: location })
    }

    updates.push({ type: AvailabilityActionType.SET_DATES_BUFFER_ENABLED, payload: Boolean(isDatesBufferEnabled) })

    // Update dates if provided
    if (dates) {
      if (dates.startDate) {
        updates.push({ type: AvailabilityActionType.SET_START_DATE, payload: dates.startDate })
      }
      if (dates.endDate) {
        updates.push({ type: AvailabilityActionType.SET_END_DATE, payload: dates.endDate })
      }
    }

    // Update flexible dates if provided
    if (typeof isFlexibleDates === 'boolean') {
      updates.push({ type: AvailabilityActionType.SET_FLEXIBLE_DATES, payload: isFlexibleDates })
    }

    // Update flexible dates options if provided
    if (flexiDatesOptions) {
      if (flexiDatesOptions.flexiDatesString) {
        updates.push({ type: AvailabilityActionType.SET_FLEXI_DATES_STRING, payload: flexiDatesOptions.flexiDatesString })
      }
      if (flexiDatesOptions.selectedMonths) {
        updates.push({ type: AvailabilityActionType.SET_SELECTED_FLEXIBLE_DATE_MONTHS, payload: flexiDatesOptions.selectedMonths })
      }
      if (flexiDatesOptions.numberOfNights) {
        updates.push({ type: AvailabilityActionType.SET_NUMBER_OF_NIGHTS, payload: flexiDatesOptions.numberOfNights })
      }
    }

    // Update bonus time dates if provided
    if (typeof isBonusTimeDates === 'boolean') {
      updates.push({ type: AvailabilityActionType.SET_BONUS_TIME_DATES, payload: isBonusTimeDates })
      if (bonusTimeOptions) {
        if (bonusTimeOptions.bonusTimeDatesString) {
          updates.push({ type: AvailabilityActionType.SET_BONUS_TIME_DATES_STRING, payload: bonusTimeOptions.bonusTimeDatesString })
        }
        if (bonusTimeOptions.numberOfNights) {
          updates.push({ type: AvailabilityActionType.SET_NUMBER_OF_NIGHTS, payload: bonusTimeOptions.numberOfNights })
        }
      }
    }

    // Update filters if provided
    if (filterList) {
      updates.push({ type: AvailabilityActionType.SET_SAVED_SEARCH_FILTERS_PAYLOAD, payload: filterList })
    }

    // Batch dispatch all updates together
    if (updates.length > 0) {
      updates.forEach((action) => this.dispatch(action))
    }

    // Saved-search execution bypasses the location autocomplete flow, so onLocationChange's
    // filter-prime never runs. Mirror onLocationChange here so dateless saved searches
    // surface the filter button just like a fresh dateless search would.
    if (location?.locationPath && !this.state.filters) {
      this.fetchFilters(location.locationPath).catch((error) => {
        logger.error('Error fetching filters during saved-search execution:', error)
      })
    }
  }

  public getContextProps(): AvailabilityContextProps {
    return {
      ...this.state,
      dispatchAvailabilityAction: this.dispatch,
      onDateChange: this.onDateChange.bind(this),
      onMonthChange: this.onMonthChange.bind(this),
      resetDates: this.resetDates.bind(this),
      resetLocation: this.resetLocation.bind(this),
      resetFlexibleDates: this.resetFlexibleDates.bind(this),
      resetBonusTimeDates: this.resetBonusTimeDates.bind(this),
      registerBonusTimeState: this.registerBonusTimeState.bind(this),
      onLocationChange: this.onLocationChange.bind(this),
      onFlexibleDatesChange: this.onFlexibleDatesChange.bind(this),
      onBonusTimeDatesChange: this.onBonusTimeDatesChange.bind(this),
      setDatesBufferEnabled: this.setDatesBufferEnabled.bind(this),
      setSelectedFlexibleDateMonths: this.setSelectedFlexibleDateMonths.bind(this),
      setNumberOfNights: this.setNumberOfNights.bind(this),
      formattedDates: this.getFormattedDates(),
      setFlexiDatesString: this.setFlexiDatesString.bind(this),
      setBonusTimeDatesString: this.setBonusTimeDatesString.bind(this),
      setAvailableSuites: this.setAvailableSuites.bind(this),
      setSelectedSuite: this.setSelectedSuite.bind(this),
      setSelectedResort: this.setSelectedResort.bind(this),
      setSavedSearch: this.setSavedSearch.bind(this),
      setLoadingState: this.setLoadingState.bind(this),
      getFormattedDates: this.getFormattedDates.bind(this),
      fetchSavedSearches: this.fetchSavedSearches.bind(this),
      createSaveSearchPayload: this.createSaveSearchPayload.bind(this),
      storeSavedSearch: this.submitSavedSearch.bind(this),
      deleteSavedSearch: this.deleteSavedSearch.bind(this),
      searchResorts: this.searchResorts.bind(this),
      searchResortAvail: this.searchResortAvail.bind(this),
      setFilters: this.setFilters.bind(this),
      updateFiltersFromResponse: this.updateFiltersFromResponse.bind(this),
      fetchFilters: this.fetchFilters.bind(this),
      setUnfilteredSearchResults: this.setUnfilteredSearchResults.bind(this),
      batchUpdateSearchCriteria: this.batchUpdateSearchCriteria.bind(this),
      setIsSavedSearchExecution: this.setIsSavedSearchExecution.bind(this),
    } as AvailabilityContextProps
  }

  public onLocationChange(location: ISearchLocation): void {
    this.dispatch({ type: AvailabilityActionType.SET_LOCATION, payload: location })

    if (location?.locationPath) {
      if (!this.state.filters) {
        this.fetchFilters(location.locationPath).catch((error) => {
          logger.error('Error fetching filters after location change:', error)
        })
      }
    }
  }

  public registerBonusTimeState(isBonusTimeEnabled: boolean, isExoticBonusTimeEnabled: boolean): void {
    this.dispatch({ type: AvailabilityActionType.SET_BONUS_TIME_STATE, payload: { isBonusTimeEnabled, isExoticBonusTimeEnabled } })
  }

  public onDateChange(dateSelection: Date | (Date | null)[], currStart = this.state.startDate, currEnd = this.state.endDate): void {
    if (!dateSelection) {
      this.dispatch({ type: AvailabilityActionType.RESET_DATES })
      return
    }

    if (Array.isArray(dateSelection)) {
      dateSelection.sort((a: Date | null, b: Date | null) => {
        if (a === null) return 1 // null goes after
        if (b === null) return -1 // null goes after
        return a.getTime() - b.getTime()
      })
    }

    // Handle array of dates (from calendar component)
    if (Array.isArray(dateSelection)) {
      const validDates = dateSelection.filter((date): date is Date => date !== null)

      if (validDates.length === 0) {
        return
      }

      if (validDates.length === 1) {
        // Single date selected
        const selectedDate = validDates[0]

        if (!currStart) {
          this.dispatch({ type: AvailabilityActionType.SET_START_DATE, payload: selectedDate })
          return
        }

        if (currStart && !currEnd) {
          if (compareDates(selectedDate, currStart) === 0) return
          this.dispatch({ type: AvailabilityActionType.SET_END_DATE, payload: selectedDate })
          return
        }

        if (currStart && currEnd) {
          this.dispatch({ type: AvailabilityActionType.SET_START_DATE, payload: selectedDate })
          this.dispatch({ type: AvailabilityActionType.SET_END_DATE, payload: null })
        }
      } else if (validDates.length === 2) {
        // Two dates selected - set both start and end
        const [startDate, endDate] = validDates.sort((a, b) => a.getTime() - b.getTime())
        this.dispatch({ type: AvailabilityActionType.SET_START_DATE, payload: startDate })
        this.dispatch({ type: AvailabilityActionType.SET_END_DATE, payload: endDate })
      }
      return
    }

    // Handle single date (fallback)
    const selectedDate = dateSelection as Date
    if (!selectedDate) return

    if (!currStart) {
      this.dispatch({ type: AvailabilityActionType.SET_START_DATE, payload: selectedDate })
      return
    }

    if (currStart && !currEnd) {
      if (compareDates(selectedDate, currStart) === 0) return
      this.dispatch({ type: AvailabilityActionType.SET_END_DATE, payload: selectedDate })
      return
    }

    if (currStart && currEnd) {
      this.dispatch({ type: AvailabilityActionType.SET_START_DATE, payload: selectedDate })
      this.dispatch({ type: AvailabilityActionType.SET_END_DATE, payload: null })
    }
  }

  public onMonthChange(date: Date | (Date | null)[]): void {
    const selectedDate = Array.isArray(date) ? date[0] || date[1] : date
    if (selectedDate) {
      this.dispatch({ type: AvailabilityActionType.SET_CURRENT_MONTH, payload: selectedDate })
    }
  }

  public resetDates(): void {
    this.dispatch({ type: AvailabilityActionType.RESET_DATES })
  }

  public resetLocation(): void {
    this.dispatch({ type: AvailabilityActionType.RESET_LOCATION })
  }

  public resetFlexibleDates(): void {
    this.dispatch({ type: AvailabilityActionType.RESET_FLEXIBLE_DATES })
  }

  public resetBonusTimeDates(): void {
    this.dispatch({ type: AvailabilityActionType.RESET_BONUS_TIME_DATES })
  }

  public onFlexibleDatesChange(isFlexiDates: boolean): void {
    this.dispatch({ type: AvailabilityActionType.SET_FLEXIBLE_DATES, payload: isFlexiDates })
  }

  public onBonusTimeDatesChange(isBonusTimeDates: boolean): void {
    this.dispatch({ type: AvailabilityActionType.SET_BONUS_TIME_DATES, payload: isBonusTimeDates })
  }

  public setDatesBufferEnabled(isDatesBufferEnabled: boolean): void {
    this.dispatch({ type: AvailabilityActionType.SET_DATES_BUFFER_ENABLED, payload: isDatesBufferEnabled })
  }

  public setSelectedFlexibleDateMonths(selectedMonths: Date[]): void {
    this.dispatch({ type: AvailabilityActionType.SET_SELECTED_FLEXIBLE_DATE_MONTHS, payload: selectedMonths })
  }

  public setNumberOfNights(numberOfNights: number): void {
    this.dispatch({ type: AvailabilityActionType.SET_NUMBER_OF_NIGHTS, payload: numberOfNights })
  }

  public setFlexiDatesString(flexiDatesString: string): void {
    this.dispatch({ type: AvailabilityActionType.SET_FLEXI_DATES_STRING, payload: flexiDatesString })
  }

  public setBonusTimeDatesString(bonusTimeDatesString: string): void {
    this.dispatch({ type: AvailabilityActionType.SET_BONUS_TIME_DATES_STRING, payload: bonusTimeDatesString })
  }

  public setAvailableSuites(suites: TMkAvailableSuite[]): void {
    this.dispatch({ type: AvailabilityActionType.SET_AVAILABLE_SUITES, payload: suites })
  }

  public setSelectedSuite(suiteId: string | null): void {
    this.dispatch({ type: AvailabilityActionType.SET_SELECTED_SUITE, payload: suiteId })
  }

  public setSelectedResort(resortKey: string | null): void {
    this.dispatch({ type: AvailabilityActionType.SET_SELECTED_RESORT, payload: resortKey })
  }

  public setSavedSearch(savedSearch: SaveSearchPayload): void {
    this.dispatch({ type: AvailabilityActionType.SET_SAVED_SEARCH, payload: savedSearch })
  }

  public setIsSavedSearchExecution(isSavedSearchExecution: boolean): void {
    this.dispatch({ type: AvailabilityActionType.SET_IS_SAVED_SEARCH_EXECUTION, payload: isSavedSearchExecution })
  }

  public getFormattedDates(): string {
    const { startDate, endDate, isFlexibleDates, flexiDatesString, bonusTimeDatesString, isBonusTimeDates, selectedFlexibleDateMonths, numberOfNights } = this.state

    if (isBonusTimeDates) {
      if (bonusTimeDatesString) {
        return bonusTimeDatesString
      }
    }
    if (isFlexibleDates) {
      if (flexiDatesString) {
        return flexiDatesString
      }
      const fallback = buildFlexiDatesString(numberOfNights, selectedFlexibleDateMonths, 'nights')
      if (fallback) return fallback
      return ''
    }

    if (startDate && endDate) {
      return formatDateRange(startDate, endDate)
    }

    return ''
  }

  public setLoadingState(isLoading: boolean): void {
    this.dispatch({ type: AvailabilityActionType.SET_LOADING_STATE, payload: isLoading })
  }

  public async fetchSavedSearches(userName: string, brand: string, accessCode: string, jwtToken: string): Promise<MkSavedSearch[]> {
    this.dispatch({ type: AvailabilityActionType.SET_LOADING_STATE, payload: true })
    try {
      if (!userName || !brand) {
        throw new Error('User name and brand are required to fetch saved searches')
      }

      if (!jwtToken || !accessCode) {
        throw new Error('Missing authentication information')
      }

      const url = new URL(`${serverUrl}${API}/search/savedSearches`)
      url.searchParams.append('userName', userName)
      url.searchParams.append('brand', brand)

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-JWT-TOKEN': jwtToken,
          'access-code': accessCode,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch saved searches: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()

      this.dispatch({
        type: AvailabilityActionType.SET_SEARCH_RESPONSE_META,
        payload: {
          alertMessage: data?.alertMessage || '',
          businessSystemCode: data?.businessSystemCode || '',
        },
      })
      this.dispatch({ type: AvailabilityActionType.FETCH_SAVED_SEARCHES_SUCCESS, payload: data })
      return data
    } catch (error) {
      this.dispatch({
        type: AvailabilityActionType.FETCH_SAVED_SEARCHES_FAILURE,
        payload: error instanceof Error ? error : new Error('An unknown error occurred'),
      })
      throw error
    }
  }

  public createSaveSearchPayload(searchName?: string): SaveSearchPayload {
    const { location, startDate, endDate, monthYears, isFlexibleDates, numberOfNights, filters } = this.state

    return {
      searchLocation: location?.locationPath || '',
      name: searchName || location?.title || 'My Resort Search',
      searchDateStart: startDate && !isFlexibleDates ? startDate.toISOString() : '',
      searchDateEnd: endDate && !isFlexibleDates ? endDate.toISOString() : '',
      startDate: startDate ? startDate.toISOString() : '',
      endDate: endDate ? endDate.toISOString() : '',
      monthYears: monthYears || '',
      numberOfNights: numberOfNights || 0,
      sortOrder: 'default',
      ...(location?.item.locationType === ELocationType.STATE && { state: location?.title || '' }),
      ...(location?.item.locationType === ELocationType.CITY && { city: location?.title || '' }),
      ...(location?.item.locationType !== ELocationType.STATE && location?.item.locationType !== ELocationType.CITY && { region: location?.title || '' }),
      filters: filters || [],
    }
  }

  public async submitSavedSearch(userName: string, brand: string, accessCode: string, jwtToken: string, payload?: SaveSearchPayload): Promise<any> {
    this.dispatch({ type: AvailabilityActionType.SET_LOADING_STATE, payload: true })

    try {
      // If no payload is provided, create one from the current state
      const searchPayload = {
        ...this.createSaveSearchPayload(),
        ...payload, // Merge with provided payload if any
      }

      // Validate required fields. Allow location-only (dateless) saves alongside specific-dates and flexible-dates.
      const isFlexible = Boolean(searchPayload.searchForFlexibleDates)
      const hasSpecificDates = Boolean(searchPayload.startDate && searchPayload.endDate)
      const hasNoDates = !searchPayload.startDate && !searchPayload.endDate
      const isPartialDates = !hasSpecificDates && !hasNoDates
      if (!searchPayload.searchLocation) {
        throw new Error('Search location is required to save a search')
      }
      if (!isFlexible && isPartialDates) {
        throw new Error('Both start and end dates are required to save a search with specific dates')
      }

      const url = new URL(`${serverUrl}${API}/search/savedSearches`)
      url.searchParams.append('userName', userName)
      // url.searchParams.append('brand', brandName(brand))
      url.searchParams.append('brand', brand)

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(searchPayload),
      })

      if (!response.ok) {
        if (response.status === 422) {
          const errorMessage = await response.text()
          throw new Error(errorMessage)
        } else {
          throw new Error(`Failed to save search: ${response.status} ${response.statusText}`)
        }
      }

      const data = await response.json()
      this.dispatch({ type: AvailabilityActionType.SAVE_SEARCH_SUCCESS, payload: searchPayload })

      // Update saved searches after successful save
      await this.fetchSavedSearches(userName, brand, accessCode, jwtToken)

      return data
    } catch (error) {
      this.dispatch({
        type: AvailabilityActionType.SAVE_SEARCH_FAILURE,
        payload: error instanceof Error ? error : new Error('An unknown error occurred'),
      })
      throw error
    }
  }

  public checkAddDefaultFilters(filterList: MkFilterCategory[]): MkFilterCategory[] {
    if (filterList.length === 0) {
      filterList = [
        {
          category: 'special',
          filters: [
            {
              key: 'showClubPassResorts',
              state: true,
              count: 0,
            },
          ],
        },
      ]
    }
    return filterList
  }

  public async searchResorts(
    options: {
      pageNum?: number
      pageSize?: number
      filterList?: MkFilterCategory[]
      sortBy?: string
      latitude?: string
      longitude?: string
    } = {},
  ): Promise<MkSearchResponse> {
    try {
      const { location, startDate, endDate, isFlexibleDates, isBonusTimeDates, isDatesBufferEnabled, monthYears, numberOfNights, selectedFlexibleDateMonths } = this.state

      const latOpt = options.latitude?.trim() ?? ''
      const lngOpt = options.longitude?.trim() ?? ''
      const hasGeoOptions = latOpt !== '' && lngOpt !== ''
      /** Map/geo search uses lat/lng only; do not merge prior destination fields (state may be stale right after resetLocation). */
      const geoOnlySearch = hasGeoOptions

      if (!location?.locationPath && !hasGeoOptions) {
        throw new Error('Location is required for search')
      }

      // Determine which date type to prioritize based on isFlexibleDates flag
      const shouldUseFlexibleDates = isFlexibleDates && selectedFlexibleDateMonths.length > 0 && numberOfNights > 0
      const shouldUseSpecificDates = !isFlexibleDates && startDate && endDate

      let formattedMonthYears = monthYears || ''
      if (shouldUseFlexibleDates) {
        formattedMonthYears = selectedFlexibleDateMonths
          .map((date) => {
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const year = date.getFullYear().toString()
            return `${month}-${year}`
          })
          .join(',')
      }

      let filterList = options?.filterList ?? (this.state.isSavedSearchExecution ? this.state.savedSearchFiltersPayload : [])
      filterList = this.checkAddDefaultFilters(filterList)

      // Use the location type information to determine which location fields to set
      const locationType = location?.item?.locationType || ELocationType.REGION
      const locationValue = geoOnlySearch
        ? ''
        : location?.item
          ? locationType === ELocationType.REGION
            ? location.title || '' // Use title for regions to preserve spaces
            : locationType === ELocationType.CITY
              ? location.item.city
              : locationType === ELocationType.STATE
                ? location.item.state
                : locationType === ELocationType.COUNTRY
                  ? location.item.country
                  : location.title || ''
          : location?.title || ''

      // Create the searchRequest with the appropriate location fields
      const searchRequest: MkSearchRequest = {
        locationPath: geoOnlySearch ? '' : (location?.locationPath ?? ''),
        // Use specific dates only if we're not in flexible dates mode and have valid dates
        checkInDate: shouldUseSpecificDates ? startDate.toISOString() : '',
        checkOutDate: shouldUseSpecificDates ? endDate.toISOString() : '',
        // Use flexible dates parameters only if we're in flexible dates mode and have valid data
        isFlexibleDates: shouldUseFlexibleDates,
        bonusTime: isBonusTimeDates,
        isDatesBufferEnabled,
        monthYears: shouldUseFlexibleDates ? formattedMonthYears : '',
        numberOfNights: shouldUseFlexibleDates ? numberOfNights : 0,
        filterList,
        pageNum: options?.pageNum || 1,
        pageSize: options?.pageSize || 10,
        sortBy: options?.sortBy,
      }

      if (hasGeoOptions) {
        searchRequest.latitude = latOpt
        searchRequest.longitude = lngOpt
      }

      // Add the correct location field based on the location type using a mapping
      if (locationValue && !geoOnlySearch) {
        const locationFieldMap: { [key: string]: string } = {
          REGION: 'region',
          CITY: 'city',
          STATE: 'state',
          COUNTRY: 'country',
        }

        // Only use valid location fields, default to 'region' if type not recognized
        const fieldName = locationFieldMap[locationType] || 'region'
        if (fieldName) {
          searchRequest[fieldName] = locationValue
        }
      }

      const url = `${serverUrl}${API_SEARCH_RESORT_LIST}`

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(searchRequest),
      })

      if (!response.ok) {
        throw new Error(`Failed to search resorts: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()

      data.alertMessage = data?.alertMessage || ''
      data.businessSystemCode = data?.businessSystemCode || ''

      this.dispatch({
        type: AvailabilityActionType.SET_SEARCH_RESPONSE_META,
        payload: {
          alertMessage: data?.alertMessage || '',
          businessSystemCode: data?.businessSystemCode || '',
        },
      })

      // Store unfiltered search results if no filters are applied (only default filters)
      const isUnfilteredSearch =
        filterList.length === 1 &&
        filterList[0].category === 'special' &&
        filterList[0].filters.length === 1 &&
        filterList[0].filters[0].key === 'showClubPassResorts' &&
        filterList[0].filters[0].state === true

      if (isUnfilteredSearch) {
        this.setUnfilteredSearchResults(data as MkSearchResponse)
      }

      return data as MkSearchResponse
    } catch (error) {
      logger.error('Search resorts error:', error)
      throw error
    }
  }

  public async searchResortAvail(request: MkResortAvailRequest): Promise<MkResortAvailResponse> {
    this.dispatch({ type: AvailabilityActionType.SET_LOADING_STATE, payload: true })

    try {
      if (this.state.filters?.sort?.active && !request.sortBy) {
        request.sortBy = this.state.filters.sort.active
      }

      const authHeaders = this.getAuthHeaders()
      const response = await searchResortAvail(request, authHeaders)
      this.dispatch({ type: AvailabilityActionType.SET_LOADING_STATE, payload: false })
      return response
    } catch (error) {
      this.dispatch({ type: AvailabilityActionType.SET_LOADING_STATE, payload: false })
      throw error
    }
  }

  public async deleteSavedSearch(userName: string, brand: string, searchId: string, accessCode: string, jwtToken: string): Promise<boolean> {
    try {
      if (!searchId || !userName || !brand) {
        throw new Error('Search ID, user name, and brand are required to delete a saved search')
      }

      const url = new URL(`${serverUrl}${API}/search/savedSearches`)
      url.searchParams.append('userName', userName)
      url.searchParams.append('brand', brand)
      url.searchParams.append('savedSearchId', searchId)

      const response = await fetch(url.toString(), {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error(`Failed to delete saved search: ${response.status} ${response.statusText}`)
      }

      this.dispatch({
        type: AvailabilityActionType.DELETE_SAVED_SEARCH_SUCCESS,
        payload: searchId,
      })

      return true
    } catch (error) {
      this.dispatch({
        type: AvailabilityActionType.DELETE_SAVED_SEARCH_FAILURE,
        payload: error instanceof Error ? error : new Error('An unknown error occurred'),
      })
      throw error
    }
  }

  public setFilters(filters: any): void {
    this.dispatch({ type: AvailabilityActionType.SET_FILTERS, payload: filters })
  }

  public setUnfilteredSearchResults(results: MkSearchResponse): void {
    this.dispatch({ type: AvailabilityActionType.SET_UNFILTERED_SEARCH_RESULTS, payload: results })
  }

  public updateFiltersFromResponse(response: any): void {
    if (!response || !response.filterList || !Array.isArray(response.filterList)) {
      return
    }

    const filters: Filters & { resortList: unknown[] } = {
      ...buildFiltersFromCategories(response.filterList),
      resortList: response.resortList || [],
    }

    // Add the showAvailableOnly toggle if the response didn't include it.
    if (!filters.controls[FILTER_SECTION_SPECIAL]?.showAvailableOnly) {
      if (!filters.controls[FILTER_SECTION_SPECIAL]) {
        filters.controls[FILTER_SECTION_SPECIAL] = {}
        filters.state[FILTER_SECTION_SPECIAL] = {}
        filters.sections.push({
          name: ACCESSIBILITY_FILTER_NAME,
          key: FILTER_SECTION_SPECIAL,
        })
      }
      filters.controls[FILTER_SECTION_SPECIAL].showAvailableOnly = {
        hidden: false,
        label: SHOW_AVAILABLE_RESORTS_ONLY,
        type: 'switch',
        icon: null,
      } as any
      filters.state[FILTER_SECTION_SPECIAL].showAvailableOnly = {
        value: false,
      }
    }

    let filtersPayload: typeof filters = filters
    if (filters.state[FILTER_SECTION_SPECIAL]?.showAvailableOnly) {
      const availabilityCount = countResortsWithAvailabilityFromResortList(response.resortList)
      filtersPayload = mergeShowAvailableOnlyCount(filters, availabilityCount)
    }

    this.dispatch({ type: AvailabilityActionType.UPDATE_FILTERS_FROM_RESPONSE, payload: filtersPayload })
  }

  /**
   * @param locationPath - Region path from search, or `undefined` to use current state location.
   *   Pass `null` to fetch default filters with no location (GET filters with no query param).
   */
  public async fetchFilters(locationPath?: string | null): Promise<void> {
    try {
      // Check if user is authorized using the standard authentication pattern
      if (!this.isAuthorized()) {
        return
      }

      const headers = this.getAuthHeaders()
      const fetchGlobal = locationPath === null
      const effectiveLocationPath = fetchGlobal ? undefined : locationPath !== undefined ? locationPath : this.state.location?.locationPath

      if (!fetchGlobal && !effectiveLocationPath) {
        return
      }

      const data = await apiFetchFilters<MkFilterCategory[]>(headers, fetchGlobal ? undefined : effectiveLocationPath)

      if (data && Array.isArray(data)) {
        const filters = buildFiltersFromCategories(data)

        // Never overwrite with empty: when fetchFilters API returns no sections, skip setFilters so we don't
        // wipe filters that were already set by the search response (fetchFilters can complete after search).
        if (filters.sections.length > 0) {
          this.setFilters(filters)
        }
      }
    } catch (error) {
      logger.error('Error fetching filters:', error)
    }
  }
}
